import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadService, checkHealth, assetService } from '../services';
import { CheckCircle, AlertCircle, Image as ImageIcon, Sparkles, X, Building2, ArrowLeft, FolderOpen, Check } from 'lucide-react';
import Button from '../components/Button';
import Card from '../components/Card';
import { useBrandSettings } from '../hooks/useSupabase';
import { useAuth } from '../contexts/AuthContext';
import StepProgress from '../components/StepProgress';
import { Asset } from '../services/assetService';
import { mockupService } from '../services/mockupService';
import { useAuthRefresh } from '../hooks/useAuthRefresh';
import MockupFlowSummary from '../components/MockupFlowSummary';
import {
  formatTemplateName,
  readSelectedTemplateDetails,
  resolveTemplateThumbnailUrl,
  type SelectedTemplateDetails,
  writeSelectedTemplateDetails,
} from '../utils/mockupFlow';

interface FileState {
  file: File | null;
  previewUrl: string;
  id: string;
  uploadUrl?: string;
  storagePath?: string;
  isUploading: boolean;
  sourceType: 'none' | 'upload' | 'asset' | 'brand-logo';
}

const Create: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { brandSettings } = useBrandSettings();
  
  const [primaryImage, setPrimaryImage] = useState<FileState>({ file: null, previewUrl: '', id: '', isUploading: false, sourceType: 'none' });
  const [secondaryImage, setSecondaryImage] = useState<FileState>({ file: null, previewUrl: '', id: '', isUploading: false, sourceType: 'none' });
  
  const [uploadError, setUploadError] = useState<string>('');
  const [useBrandLogo, setUseBrandLogo] = useState(false);
  const [showAssetSelector, setShowAssetSelector] = useState<'primary' | 'secondary' | null>(null);
  const [userAssets, setUserAssets] = useState<Asset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  
  const primaryFileInputRef = useRef<HTMLInputElement>(null);
  const secondaryFileInputRef = useRef<HTMLInputElement>(null);

  // Use auth refresh hook to reload data when session refreshes
  useAuthRefresh(() => {
    if (user && showAssetSelector) {
      console.log('Auth refreshed, reloading user assets...');
      loadUserAssets();
    }
  });

  // Check API health on mount
  useEffect(() => {
    checkHealth().catch(() => setUploadError('Backend server is not available. Please ensure it is running.'));
  }, []);

  // Load user assets when selector is opened
  const loadUserAssets = useCallback(async () => {
    if (!user) return;
    
    setLoadingAssets(true);
    try {
      const { assets, error } = await assetService.getUserAssets({ limit: 50 });
      if (!error) {
        setUserAssets(assets);
      }
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  }, [user]);

  useEffect(() => {
    if (showAssetSelector) {
      loadUserAssets();
    }
  }, [showAssetSelector, loadUserAssets]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, imageType: 'primary' | 'secondary') => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0], imageType);
    }
  };

  const handleFileUpload = async (file: File, imageType: 'primary' | 'secondary') => {
    setUploadError('');
    
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a PNG, JPG, SVG, or WEBP file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    const setImageState = imageType === 'primary' ? setPrimaryImage : setSecondaryImage;
    
    setImageState(prev => ({ ...prev, isUploading: true, file }));

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageState(prev => ({ ...prev, previewUrl: reader.result as string }));
    };
    reader.readAsDataURL(file);

    try {
      const payload = {
        primaryImage: imageType === 'primary' ? file : primaryImage.file,
        secondaryImage: imageType === 'secondary' ? file : secondaryImage.file
      };
      
      const response = await uploadService.uploadFiles(payload);
      
      if (response.success && response.files) {
        response.files.forEach(uploadedFile => {
          if (uploadedFile.fieldName === 'primaryImage') {
            setPrimaryImage(prev => ({
              ...prev,
              id: uploadedFile.id,
              uploadUrl: uploadedFile.url,
              storagePath: uploadedFile.storagePath,
              isUploading: false,
              sourceType: 'upload'
            }));
          } else if (uploadedFile.fieldName === 'secondaryImage') {
            setSecondaryImage(prev => ({
              ...prev,
              id: uploadedFile.id,
              uploadUrl: uploadedFile.url,
              storagePath: uploadedFile.storagePath,
              isUploading: false,
              sourceType: 'upload'
            }));
          }
        });
      } else {
        throw new Error(response.error || 'Invalid response from server');
      }
    } catch (error: any) {
      setUploadError(error.message || 'Failed to upload file.');
      setImageState(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleAssetSelect = async (asset: Asset, imageType: 'primary' | 'secondary') => {
    const imageState: FileState = {
      file: null,
      previewUrl: asset.file_url,
      id: asset.id,
      uploadUrl: asset.file_url,
      isUploading: false,
      sourceType: 'asset'
    };

    if (imageType === 'primary') {
      setPrimaryImage(imageState);
      setUseBrandLogo(false);
      // Track usage
      await assetService.incrementUsageCount(asset.id);
    } else {
      setSecondaryImage(imageState);
    }

    setShowAssetSelector(null);
  };

  const handleRemove = async (imageType: 'primary' | 'secondary') => {
    const { id } = imageType === 'primary' ? primaryImage : secondaryImage;
    const setImageState = imageType === 'primary' ? setPrimaryImage : setSecondaryImage;

    if (id && (imageType === 'primary' ? primaryImage.sourceType : secondaryImage.sourceType) === 'upload') {
      try {
        await uploadService.deleteFile(id);
      } catch (error) {
        console.error('Failed to delete file from server:', error);
      }
    }
    setImageState({ file: null, previewUrl: '', id: '', isUploading: false, sourceType: 'none' });
    
    // Clear from sessionStorage when removing secondary image
    if (imageType === 'secondary') {
      sessionStorage.removeItem('secondaryImageId');
    }
  };

  // Check if template is already selected + validate stale session data
  useEffect(() => {
    const selectedTemplate = sessionStorage.getItem('selectedTemplate');
    const assetImageId = sessionStorage.getItem('assetImageId');
    const assetImageUrl = sessionStorage.getItem('assetImageUrl');

    console.log('Create page - selectedTemplate:', selectedTemplate);
    console.log('Create page - sessionStorage:', {...sessionStorage});

    // If coming from asset manager, pre-populate the primary image
    if (assetImageId && assetImageUrl) {
      setPrimaryImage({
        file: null,
        previewUrl: assetImageUrl,
        id: assetImageId,
        uploadUrl: assetImageUrl,
        isUploading: false,
        sourceType: 'asset'
      });
      // Clear asset session data after using it
      sessionStorage.removeItem('assetImageId');
      sessionStorage.removeItem('assetImageUrl');
      sessionStorage.removeItem('assetImageType');
    }

    // Validate stale primaryImageId in session — verify file still exists
    const storedPrimaryId = sessionStorage.getItem('primaryImageId');
    const storedPrimaryUrl = sessionStorage.getItem('primaryImageUrl');
    if (storedPrimaryId && storedPrimaryId !== 'brand-logo' && storedPrimaryUrl) {
      fetch(storedPrimaryUrl, { method: 'HEAD' })
        .then(res => {
          if (!res.ok) {
            sessionStorage.removeItem('primaryImageId');
            sessionStorage.removeItem('primaryImageUrl');
            sessionStorage.removeItem('secondaryImageId');
            sessionStorage.removeItem('secondaryImageUrl');
            setUploadError('Your previous upload has expired. Please upload again.');
          }
        })
        .catch(() => {
          // Network error — don't clear, might be offline
        });
    }

    if (!selectedTemplate) {
      // If no template selected, go to templates first
      console.log('No template selected, redirecting to mockups page...');
      navigate('/dashboard/mockups');
      return;
    }

    sessionStorage.setItem('creationStep', '2');
  }, [navigate]);

  const handleContinue = () => {
    if (primaryImage.id || (useBrandLogo && primaryImage.previewUrl)) {
      // Handle brand logo case
      if (useBrandLogo) {
        sessionStorage.setItem('primaryImageId', 'brand-logo');
        sessionStorage.setItem('primaryImageUrl', primaryImage.uploadUrl || primaryImage.previewUrl);
        sessionStorage.setItem('useBrandLogo', 'true');
      } else if (primaryImage.uploadUrl) {
        sessionStorage.setItem('primaryImageId', primaryImage.id);
        sessionStorage.setItem('primaryImageUrl', primaryImage.uploadUrl);
        sessionStorage.setItem('isAssetLibrary', primaryImage.sourceType === 'asset' ? 'true' : 'false');
      } else {
        sessionStorage.setItem('primaryImageId', primaryImage.id);
        sessionStorage.removeItem('primaryImageUrl');
        sessionStorage.removeItem('useBrandLogo');
        sessionStorage.removeItem('isAssetLibrary');
      }
      
      if (secondaryImage.id) {
        if (secondaryImage.uploadUrl) {
          sessionStorage.setItem('secondaryImageId', secondaryImage.id);
          sessionStorage.setItem('secondaryImageUrl', secondaryImage.uploadUrl);
        } else {
          sessionStorage.setItem('secondaryImageId', secondaryImage.id);
          sessionStorage.removeItem('secondaryImageUrl');
        }
      } else {
        // Ensure no stale secondary image ID remains in storage
        sessionStorage.removeItem('secondaryImageId');
        sessionStorage.removeItem('secondaryImageUrl');
      }
      
      // Move to customization step
      sessionStorage.setItem('creationStep', '2');
      navigate('/dashboard/customize');
    } else {
      setUploadError('Please upload a primary image to continue.');
    }
  };

  const isUploading = primaryImage.isUploading || secondaryImage.isUploading;
  const canContinue = (primaryImage.id || (useBrandLogo && primaryImage.previewUrl)) && !isUploading;

  const selectedTemplate = sessionStorage.getItem('selectedTemplate');
  const selectedTemplateLabel = formatTemplateName(selectedTemplate);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState<SelectedTemplateDetails | null>(() => readSelectedTemplateDetails());
  const selectedTemplateThumbnailUrl = resolveTemplateThumbnailUrl(selectedTemplateDetails?.thumbnailUrl);

  useEffect(() => {
    if (!selectedTemplate) {
      setSelectedTemplateDetails(null);
      return;
    }

    const storedDetails = readSelectedTemplateDetails();
    if (storedDetails?.id === selectedTemplate) {
      setSelectedTemplateDetails(storedDetails);
      if (storedDetails.thumbnailUrl) {
        return;
      }
    }

    let isActive = true;

    const hydrateTemplateDetails = async () => {
      try {
        const response = await mockupService.getTemplates();
        if (!response.success) {
          return;
        }

        const matchedTemplate = response.templates.find((template) => template.id === selectedTemplate);
        if (!matchedTemplate || !isActive) {
          return;
        }

        setSelectedTemplateDetails(matchedTemplate);
        writeSelectedTemplateDetails(matchedTemplate);
      } catch (error) {
        console.error('Failed to load selected template details:', error);
      }
    };

    void hydrateTemplateDetails();

    return () => {
      isActive = false;
    };
  }, [selectedTemplate]);

  const steps = [
    { number: 1, title: 'Choose Template', description: 'Select mockup type' },
    { number: 2, title: 'Upload Assets', description: 'Add your images' },
    { number: 3, title: 'Customize', description: 'Adjust settings' },
    { number: 4, title: 'Ready', description: 'Review and export' }
  ];

  const flowSummaryItems = useMemo(() => {
    const primarySource = useBrandLogo
      ? 'Brand logo selected'
      : primaryImage.sourceType === 'asset'
        ? 'Primary asset from library'
        : primaryImage.id
          ? 'Primary file uploaded'
          : 'Primary asset missing';

    const secondarySource = secondaryImage.id
      ? secondaryImage.sourceType === 'asset'
        ? 'Secondary asset from library'
        : 'Secondary file uploaded'
      : 'Optional asset not added';

    return [
      { label: 'Template', value: selectedTemplateLabel, tone: 'default' as const },
      { label: 'Primary Asset', value: primarySource, tone: primaryImage.id || useBrandLogo ? 'success' as const : 'warning' as const },
      { label: 'Secondary Asset', value: secondarySource, tone: secondaryImage.id ? 'success' as const : 'default' as const },
      { label: 'Next Step', value: canContinue ? 'Ready for customization' : 'Upload a primary asset', tone: canContinue ? 'success' as const : 'warning' as const },
    ];
  }, [canContinue, primaryImage.id, primaryImage.sourceType, secondaryImage.id, secondaryImage.sourceType, selectedTemplateLabel, useBrandLogo]);

  return (
    <div>
      <StepProgress
        currentStep={2}
        steps={steps}
        helperText="Your template is locked in. Add the main asset now so the customization step starts with the right context."
      />
      
      <div className="max-w-content mx-auto px-6 py-12">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/dashboard/mockups')}
            className="flex items-center text-body-grey hover:text-text-dark transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Templates
          </button>
          {selectedTemplate && (
            <span className="ml-auto text-sm text-body-grey">
              Selected: <span className="font-medium text-text-dark">{selectedTemplateDetails?.name || selectedTemplateLabel}</span>
            </span>
          )}
        </div>
        
        <div className="text-center mb-12">
          <h1 className="font-heading text-h1 text-text-dark mb-4">Upload Your Assets</h1>
          <p className="text-body font-body text-body-grey">
            Upload a primary image/logo and an optional secondary image for your mockup
          </p>
        </div>

      <div className="mb-8 grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <MockupFlowSummary
          title="Flow Status"
          description="This keeps the current setup visible so users do not have to remember what is already selected."
          items={flowSummaryItems}
        />

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Selected template</p>
            <h2 className="mt-1 text-lg font-semibold text-gray-900">{selectedTemplateDetails?.name || selectedTemplateLabel}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {selectedTemplateDetails?.description || 'This mockup shell will be used when you generate.'}
            </p>
          </div>

          <div className="p-5">
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-gray-100">
              {selectedTemplateThumbnailUrl ? (
                <img
                  src={selectedTemplateThumbnailUrl}
                  alt={selectedTemplateDetails?.name || selectedTemplateLabel}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-6 text-center text-sm font-medium text-gray-500">
                  Template preview will appear here.
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent px-4 py-4">
                <p className="text-sm font-semibold text-white">{selectedTemplateLabel}</p>
                <p className="mt-1 text-xs text-white/80">Upload your asset next to see how it will be paired with this mockup type.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          {/* Brand Logo Option */}
          {user && brandSettings?.logo_url && !primaryImage.file && (
            <div className="mb-4 p-4 bg-brand-purple/10 rounded-lg border border-brand-purple/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-5 h-5 text-brand-purple" />
                  <div>
                    <p className="font-medium text-text-dark">Use your brand logo</p>
                    <p className="text-sm text-body-grey">{brandSettings.brand_name || 'Your saved brand logo'}</p>
                  </div>
                </div>
                <Button 
                  variant="secondary" 
                  size="small"
                  onClick={async () => {
                    setUseBrandLogo(true);
                    setPrimaryImage({
                      file: null,
                      previewUrl: brandSettings.logo_url!,
                      id: 'brand-logo',
                      uploadUrl: brandSettings.logo_url!,
                      isUploading: false,
                      sourceType: 'brand-logo'
                    });
                    // Store brand colors and identity in session storage for customize page
                    sessionStorage.setItem('brandColors', JSON.stringify({
                      primary: brandSettings.primary_color,
                      secondary: brandSettings.secondary_color,
                      accent: brandSettings.accent_color
                    }));
                    sessionStorage.setItem('brandSettings', JSON.stringify({
                      primary: brandSettings.primary_color,
                      secondary: brandSettings.secondary_color,
                      accent: brandSettings.accent_color,
                      brandStyle: brandSettings.brand_style,
                      brandDescription: brandSettings.brand_description,
                      industry: brandSettings.industry,
                      typographyStyle: brandSettings.typography_style,
                    }));
                  }}
                >
                  Use Brand Logo
                </Button>
              </div>
            </div>
          )}
          
          <UploadSlot
            title="Primary Image / Logo"
            description="This is the main image that will be placed on the mockup."
            imageState={primaryImage}
            fileInputRef={primaryFileInputRef}
            onFileSelect={(e) => handleFileSelect(e, 'primary')}
            onRemove={() => {
              handleRemove('primary');
              setUseBrandLogo(false);
              sessionStorage.removeItem('brandColors');
              sessionStorage.removeItem('brandSettings');
            }}
            onAssetSelect={() => setShowAssetSelector('primary')}
            icon={<ImageIcon className="w-10 h-10 text-brand-purple" />}
          />
        </div>
        
        <UploadSlot
          title="Secondary Image (Optional)"
          description="A background or texture to be combined by the AI."
          imageState={secondaryImage}
          fileInputRef={secondaryFileInputRef}
          onFileSelect={(e) => handleFileSelect(e, 'secondary')}
          onRemove={() => handleRemove('secondary')}
          onAssetSelect={() => setShowAssetSelector('secondary')}
          icon={<Sparkles className="w-10 h-10 text-brand-purple" />}
        />
      </div>

      {uploadError && (
        <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg text-center">
          <div className="flex items-center justify-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-label font-body text-red-600">{uploadError}</p>
          </div>
        </div>
      )}

      <div className="mt-12 text-center">
        <Button
          variant="primary"
          size="large"
          onClick={handleContinue}
          disabled={!canContinue}
        >
          {isUploading ? 'Uploading...' : 'Continue to Customization'}
        </Button>
      </div>
      </div>

      {/* Asset Selector Modal */}
      {showAssetSelector && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAssetSelector(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Select from Assets
              </h2>
              <button
                onClick={() => setShowAssetSelector(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {loadingAssets ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-purple"></div>
                </div>
              ) : userAssets.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No assets found</p>
                  <p className="text-sm text-gray-500 mt-2">Upload assets from the Assets page to use them here</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {userAssets.map((asset) => (
                    <button
                      key={asset.id}
                      onClick={() => handleAssetSelect(asset, showAssetSelector)}
                      className="relative group overflow-hidden rounded-lg border-2 border-gray-200 hover:border-brand-purple transition-all"
                    >
                      <div className="aspect-square bg-gray-100">
                        <img 
                          src={asset.thumbnail_url || asset.file_url} 
                          alt={asset.original_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Check className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-700 truncate">{asset.original_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{asset.category}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-component for the upload slot
interface UploadSlotProps {
  title: string;
  description: string;
  imageState: FileState;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onAssetSelect?: () => void;
  icon: React.ReactNode;
}

const UploadSlot: React.FC<UploadSlotProps> = ({ title, description, imageState, fileInputRef, onFileSelect, onRemove, onAssetSelect, icon }) => {
  const handleChooseFileClick = () => fileInputRef.current?.click();
  const FolderOpenIcon = FolderOpen; // Reference the imported icon

  return (
    <Card className="p-8">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
        onChange={onFileSelect}
      />
      {!imageState.previewUrl ? (
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
            {icon}
          </div>
          <h3 className="font-heading text-h3 text-text-dark">{title}</h3>
          <p className="text-sm text-body-grey">{description}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="secondary" size="medium" onClick={handleChooseFileClick}>
              Choose File
            </Button>
            {onAssetSelect && (
              <Button variant="secondary" size="medium" onClick={onAssetSelect}>
                <FolderOpenIcon className="w-4 h-4 mr-2" />
                From Assets
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex-shrink-0 mb-4">
            <img
              src={imageState.previewUrl}
              alt="Preview"
              className="w-full h-48 object-contain rounded-card bg-background"
            />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-h4 text-text-dark truncate mb-1" title={imageState.file?.name || (imageState.id === 'brand-logo' ? 'Brand Logo' : 'Asset from library')}>
              {imageState.file?.name || (imageState.id === 'brand-logo' ? 'Brand Logo' : 'Selected asset')}
            </h3>
            {imageState.file && (
              <p className="text-sm text-body-grey mb-4">
                Size: {(imageState.file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            )}
            {imageState.isUploading && (
              <div className="mb-4 flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-purple"></div>
                <p className="text-sm text-body-grey">Uploading...</p>
              </div>
            )}
            {imageState.id && !imageState.isUploading && (
              <div className="mb-4 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <p className="text-sm text-green-600">Upload successful</p>
              </div>
            )}
            <Button
              variant="secondary"
              size="small"
              onClick={onRemove}
              className="w-full"
            >
             <X className="w-4 h-4 mr-2" /> Remove
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default Create;
